import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib_app/api";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Building2, ListChecks, Users as UsersIcon, LayoutDashboard, KanbanSquare, BarChart3, FileText, Settings2, Target, Activity, ReceiptText, FolderKanban } from "lucide-react";
import Avatar from "@/components/app/Avatar";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { owner: "Owner", sales_director: "Sales Director", sales: "Sales", finance: "Finance" };
const STATUS_LABEL = { active: "Aktif", won: "Deal", lost: "Lost" };

// Static navigation shortcuts always available in the palette.
const NAV_ITEMS = [
  { path: "/dashboard",      label: "Dashboard",           icon: LayoutDashboard },
  { path: "/prospects",      label: "Prospek",             icon: ListChecks },
  { path: "/kanban",         label: "Kanban",              icon: KanbanSquare },
  { path: "/activity",       label: "Aktivitas",           icon: Activity },
  { path: "/projects",       label: "Proyek",             icon: FolderKanban },
  { path: "/quotations",     label: "Quotation",          icon: FileText },
  { path: "/invoices",       label: "Invoice",             icon: ReceiptText },
  { path: "/clients",        label: "Klien",              icon: Building2 },
  { path: "/reports/sales",  label: "Laporan Penjualan",        icon: BarChart3 },
  { path: "/reports/team",   label: "Rapor Saya",          icon: FileText },
  { path: "/team",           label: "Manajemen · Tim",     icon: UsersIcon },
  { path: "/targets",        label: "Manajemen · Target",  icon: Target },
  { path: "/settings?tab=profile", label: "Profile",             icon: Settings2 },
];

/**
 * Global command palette (⌘K / Ctrl+K).
 * Renders a spotlight-style search over prospects, clients, and sales,
 * plus static navigation shortcuts.
 */
export default function CommandPalette({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFinance = user?.role === "finance";
  const navItems = useMemo(
    () => (isFinance ? NAV_ITEMS.filter((n) => !["/prospects", "/kanban", "/activity", "/reports/sales", "/reports/team", "/team", "/targets"].includes(n.path)) : NAV_ITEMS),
    [isFinance],
  );

  // Fetch data lazily on first open, then keep it. Reset query on close.
  useEffect(() => {
    if (!open) { setQuery(""); return; }
    if (prospects.length || clients.length || users.length) return;
    setLoading(true);
    Promise.all([
      isFinance ? Promise.resolve([]) : api.get("/prospects").then((r) => r.data).catch(() => []),
      api.get("/clients").then((r) => r.data).catch(() => []),
      api.get("/users?include_inactive=true").then((r) => r.data).catch(() => []),
    ]).then(([p, c, u]) => {
      setProspects(p);
      setClients(c);
      setUsers(u);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Limit rendering for cmdk fuzzy search performance
  const topProspects = useMemo(() => prospects.slice(0, 80), [prospects]);
  const topClients   = useMemo(() => clients.slice(0, 80), [clients]);
  const topUsers     = useMemo(() => (isFinance ? [] : users.filter((u) => u.role !== "finance").slice(0, 40)), [users, isFinance]);

  const goto = (path) => {
    onOpenChange(false);
    // Defer navigation to avoid Dialog focus contention
    setTimeout(() => navigate(path), 0);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden asChild>
        <DialogTitle>Pencarian Global</DialogTitle>
      </VisuallyHidden>
      <CommandInput
        placeholder="Cari prospek, client, sales, atau navigasi…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>
          {loading ? "Memuat..." : "Tidak ada hasil untuk pencarian ini."}
        </CommandEmpty>

        <CommandGroup heading="Navigasi">
          {navItems.map((n) => (
            <CommandItem
              key={n.path}
              value={`nav ${n.label}`}
              onSelect={() => goto(n.path)}
              data-testid={`cp-nav-${n.path.replace(/\//g, "-")}`}
              className="gap-3 cursor-pointer"
            >
              <n.icon className="w-4 h-4 text-[#626061] shrink-0" strokeWidth={1.5} />
              <span className="text-sm">{n.label}</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Pergi</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {topProspects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Prospek (${topProspects.length})`}>
              {topProspects.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`prospect ${p.customer || ""} ${p.service || ""} ${(p.pic_name || "")}`}
                  onSelect={() => goto(`/prospects?open=${p.id}`)}
                  data-testid={`cp-prospect-${p.id}`}
                  className="gap-3 cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-lg bg-[#E4E7EE] text-[#626061] flex items-center justify-center shrink-0">
                    <ListChecks className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#090808] truncate">{p.customer}</div>
                    <div className="text-[11px] text-[#A0A3A8] truncate">
                      {p.service || "—"} · {p.owner_nickname || p.owner_name || "?"}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest shrink-0 ${
                    p.status === "won" ? "text-[#57D28A]" : p.status === "lost" ? "text-[#FF6B7D]" : "text-[#A0A3A8]"
                  }`}>{STATUS_LABEL[p.status] || "Aktif"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {topClients.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Client (${topClients.length})`}>
              {topClients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`client ${c.name || ""} ${(c.pics || []).map((x) => x.name).join(" ")}`}
                  onSelect={() => goto(`/clients?open=${c.id}`)}
                  data-testid={`cp-client-${c.id}`}
                  className="gap-3 cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-[#57D28A] flex items-center justify-center shrink-0 overflow-hidden">
                    {c.logo ? <img src={c.logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-3.5 h-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#090808] truncate">{c.name}</div>
                    <div className="text-[11px] text-[#A0A3A8] truncate capitalize">{c.category || "swasta"}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {topUsers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Tim (${topUsers.length})`}>
              {topUsers.map((u) => (
                <CommandItem
                  key={u.user_id}
                  value={`sales ${u.name || ""} ${u.nickname || ""} ${u.email || ""}`}
                  onSelect={() => goto(`/team?open=${u.user_id}`)}
                  data-testid={`cp-user-${u.user_id}`}
                  className={`gap-3 cursor-pointer ${u.is_active === false ? "opacity-60" : ""}`}
                >
                  <Avatar name={u.name} picture={u.picture} size="xs" className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#090808] truncate">{u.name}</div>
                    <div className="text-[11px] text-[#A0A3A8] truncate">{u.email}</div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#A0A3A8] shrink-0">
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
